/**
 * TV3 Play (AVOD LT).
 * Base URL resolution:
 * 1) REACT_APP_TV3_PLAY_API_BASE — explicit override
 * 2) Derive from REACT_APP_API_URL → {origin}{pathname}/tv3-play-api (e.g. …/api/tv3-play-api)
 * 3) Same-origin `/tv3-play-api` — only if static host has rewrites to play.tv3.lt (Netlify/Vercel)
 */

function getTv3PlayBaseFromApiUrl() {
  const raw = process.env.REACT_APP_API_URL;
  if (!raw || typeof raw !== 'string') return null;
  try {
    const u = new URL(raw);
    // Must keep the same path prefix as other API calls (e.g. …/api), not only origin.
    // Otherwise requests go to /tv3-play-api on the host root while Express mounts at /api/tv3-play-api.
    const basePath = (u.pathname || '').replace(/\/+$/, '');
    return `${u.origin}${basePath}/tv3-play-api`;
  } catch {
    return null;
  }
}

function getTv3ApiOrigin() {
  const fromEnv = process.env.REACT_APP_TV3_PLAY_API_BASE;
  if (fromEnv) return fromEnv.replace(/\/$/, '');
  const fromApi = getTv3PlayBaseFromApiUrl();
  if (fromApi) return fromApi;
  return '/tv3-play-api';
}

function normalizeTitle(s) {
  return (s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function titlesAlign(a, b) {
  const na = normalizeTitle(a);
  const nb = normalizeTitle(b);
  if (!na || !nb) return false;
  if (na === nb) return true;
  if (na.length < 3 || nb.length < 3) return false;
  return na.includes(nb) || nb.includes(na);
}

/** Meaningful words for overlap (skip tiny tokens). */
function titleTokens(s) {
  return normalizeTitle(s)
    .split(' ')
    .filter((w) => w.length > 2);
}

function tokenOverlapScore(textA, textB) {
  const a = new Set(titleTokens(textA));
  const b = new Set(titleTokens(textB));
  if (a.size === 0 || b.size === 0) return 0;
  let inter = 0;
  for (const x of a) if (b.has(x)) inter += 1;
  return inter / Math.max(a.size, b.size);
}

function itemMatchesContent(item, title, originalTitle) {
  const ot = item.originalTitle;
  const it = item.title;
  if (originalTitle) {
    if (titlesAlign(ot, originalTitle)) return true;
    if (titlesAlign(it, originalTitle)) return true;
  }
  if (title) {
    if (titlesAlign(it, title)) return true;
    if (titlesAlign(ot, title)) return true;
  }
  return false;
}

function pickBestMatch(items, title, originalTitle) {
  const wantBlob = [originalTitle, title].filter(Boolean).join(' ');
  const scored = (items || []).map((item) => {
    const strict = itemMatchesContent(item, title, originalTitle);
    const overlap = Math.max(
      tokenOverlapScore(wantBlob, item.originalTitle || ''),
      tokenOverlapScore(wantBlob, item.title || ''),
      tokenOverlapScore(originalTitle || '', item.originalTitle || ''),
      tokenOverlapScore(title || '', item.title || '')
    );
    const noise = (item.type === 'CLIP' || item.type === 'NEWS') && (item.searchScore || 0) < 200;
    const programmeBoost = item.type === 'PROGRAMME' ? 80000 : 0;
    let rank =
      (item.searchScore || 0) +
      (strict ? 1e9 : 0) +
      overlap * 5000 +
      programmeBoost;
    if (noise && !strict) rank -= 50000;
    return { item, strict, overlap, rank };
  });

  scored.sort((a, b) => b.rank - a.rank);
  const best = scored[0];
  if (!best) return null;
  if (best.strict) return best.item;
  if (best.overlap >= 0.35 && (best.item.searchScore || 0) > 150) return best.item;
  if ((best.item.searchScore || 0) > 600 && best.overlap >= 0.2) return best.item;
  return null;
}

function dedupeKeywordList(keywords) {
  const seen = new Set();
  const out = [];
  for (const s of keywords) {
    if (!s || typeof s !== 'string') continue;
    const t = s.trim();
    if (t.length < 2) continue;
    const k = t.toLowerCase();
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(t);
  }
  return out;
}

/** Extra search phrases when the LT overview hints at a Texas/ranger title but TMDB still shows English. */
export function buildTv3ExtraKeywordsFromOverview(overview) {
  if (!overview || typeof overview !== 'string') return [];
  const t = overview;
  const out = [];
  if (/teksaso/i.test(t) && /reindžer/i.test(t)) {
    out.push('Volkeris, Teksaso reindžeris');
    out.push('Volkeris Teksaso');
  }
  return out;
}

/**
 * TV3 LT catalogue is keyed on Lithuanian titles. When TMDB/API only return English names,
 * try well-known LT marketing titles that TV3 indexes (add more pairs as needed).
 */
function buildKnownLtAliasesFromEnglishTitles(title, originalTitle) {
  const blob = `${title || ''} ${originalTitle || ''}`.toLowerCase();
  const out = [];
  if (/walker[^a-z0-9]*texas[^a-z0-9]*ranger|texas[^a-z0-9]*ranger/.test(blob)) {
    out.push('Volkeris, Teksaso reindžeris');
    out.push('Volkeris Teksaso');
  }
  return out;
}

/** Optional TMDB `language=lt` title when the app API does not return it. */
export async function fetchTmdbLtLocalizedName(tmdbId, mediaType) {
  const key = process.env.REACT_APP_TMDB_API_KEY;
  if (!key || tmdbId == null || tmdbId === '') return null;
  const path = mediaType === 'movie' ? 'movie' : 'tv';
  try {
    const res = await fetch(
      `https://api.themoviedb.org/3/${path}/${tmdbId}?api_key=${key}&language=lt`
    );
    if (!res.ok) return null;
    const json = await res.json();
    const n = (json.name || json.title || '').trim();
    return n || null;
  } catch {
    return null;
  }
}

function buildSearchUrl(match, fallbackTitle) {
  const q = encodeURIComponent(
    (match && (match.title || match.originalTitle)) || fallbackTitle || ''
  );
  return `https://play.tv3.lt/search?query=${q}`;
}

let deviceUid;

function getDeviceUid() {
  if (typeof window === 'undefined') return 'server';
  try {
    const key = 'tv3_device_uid';
    let id = window.localStorage.getItem(key);
    if (!id) {
      id = Array.from({ length: 32 }, () =>
        Math.floor(Math.random() * 16).toString(16)
      ).join('');
      window.localStorage.setItem(key, id);
    }
    return id.replace(/-/g, '');
  } catch {
    return 'webclient';
  }
}

async function searchTv3(keyword) {
  const base = getTv3ApiOrigin();
  const params = new URLSearchParams({
    platform: 'BROWSER',
    clips: 'true',
    episodes: 'true',
    keyword,
    lang: 'LT',
    tenant: 'AVOD_LT',
  });
  const url = `${base}/api/items/search?${params.toString()}`;
  if (!deviceUid) deviceUid = getDeviceUid();

  const rid =
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}_${Math.random().toString(36).slice(2)}`;
  const correlationId = `client_${rid}`;

  const res = await fetch(url, {
    method: 'GET',
    credentials: 'omit',
    headers: {
      Accept: 'application/json',
      'api-appversion': process.env.REACT_APP_TV3_PLAY_APP_VERSION || 'a4fd9c9',
      'api-correlationid': correlationId,
      'api-deviceuid': deviceUid,
    },
  });

  if (!res.ok) return [];

  const ct = (res.headers.get('content-type') || '').toLowerCase();
  if (!ct.includes('application/json')) return [];

  let data;
  try {
    data = await res.json();
  } catch {
    return [];
  }
  return data.items || [];
}

function buildSearchKeywords(title, originalTitle) {
  const loc = (title || '').trim();
  const orig = (originalTitle || '').trim();
  const out = [];
  const seen = new Set();
  const push = (s) => {
    if (!s || s.length < 2) return;
    const k = s.toLowerCase();
    if (seen.has(k)) return;
    seen.add(k);
    out.push(s);
  };

  push(loc);
  if (orig && normalizeTitle(orig) !== normalizeTitle(loc)) push(orig);

  // Comma / punctuation variants (index may only match one form; e.g. "Walker, Texas Ranger")
  const noComma = (s) => s.replace(/,/g, '').replace(/\s+/g, ' ').trim();
  const base = [...out];
  for (const s of base) {
    if (s.includes(',')) push(noComma(s));
  }

  return out;
}

export async function fetchTv3PlayAvailability({ title, originalTitle, overview }) {
  const localized = (title || '').trim();
  const primary = (originalTitle || '').trim();
  const queries = dedupeKeywordList([
    ...buildKnownLtAliasesFromEnglishTitles(title, originalTitle),
    ...buildTv3ExtraKeywordsFromOverview(overview),
    ...buildSearchKeywords(title, originalTitle),
  ]);

  for (const keyword of queries) {
    if (keyword.length < 2) continue;
    try {
      const items = await searchTv3(keyword);
      const match = pickBestMatch(items, localized, primary || localized);
      if (match) {
        return {
          exists: true,
          programDetails: {
            url: buildSearchUrl(match, localized || primary),
            isFree: true,
            isSubscription: false,
            isRental: false,
            matchedTitle: match.title,
            originalTitle: match.originalTitle,
          },
        };
      }
    } catch {
      // Network / parse — try next keyword
    }
  }

  return { exists: false };
}
